<script setup lang="ts">
import { ToolTipEvent, ToolTipEventKey } from '@/utils/eventBusKeys/tooltipKey'
import {
  IPillLinkItem,
  IRemedy,
  ISiteRemedy,
  ITitleDescriptionItem,
  ITooltipDropdownItems,
  IToolTipSpecifications,
  TTooltipAlignment,
  TTooltipDirection,
  TTooltipDropdownActiveStyle,
  TTooltipVariant
} from '@lifk/types'
import { useEventBus } from '@vueuse/core'
import { computed, onUnmounted, ref, Ref, watch } from 'vue'

/**
 * This component should be wrapped around the control to which it applies.
 * It has no visual representation of its own, but instead triggers the appearance
 * of a tooltip that sits above everything else in the DOM to prevent issues
 * with cropping when the target item is contained in a scrolling element.
 *
 * See UIIconButton for an example of a control modified to accept a tooltip,
 * and MapControls for an example of several tooltips in action.
 *
 * When the delayTag prop is specified, the tooltip will wait half a second
 * before hiding. This allows the user time to move their cursor from the
 * tooltip target to the tooltip itself, which will prevent it from hiding.
 * In order to prevent multiple tooltips from stacking due to the delay,
 * each tooltip emits its tag when shown. Any tooltips with the same tag
 * will then instantly hide themselves, bypassing the delay.
 */
const props = withDefaults(
  defineProps<{
    /** What text should be displayed in the tooltip? */
    text?: string
    /** What site remedy should be displayed in the tooltip? */
    remedy?: ISiteRemedy | IRemedy
    /** What guidebook item should be displayed in the tooltip? */
    reference?: IPillLinkItem
    /** What title and description should be displayed in the tooltip? */
    titleDescription?: ITitleDescriptionItem
    /** What menu items should be displayed in the tooltip, when the dropdown variant is active? */
    dropdownItems?: ITooltipDropdownItems
    /** In what direction should the tooltip extend away from the target? */
    direction: TTooltipDirection
    /** How should the tooltip be aligned relative to the target? */
    alignment: TTooltipAlignment
    /** What should the maximum width of the tooltip be? */
    maxWidthPx?: number
    /** What should the offset be between the target and the tooltip? */
    offset?: number
    /** Is the target element full width? (This is set to false for elements like progress bars) */
    fullWidthTarget?: boolean
    /** What variant of the tooltip should be used? */
    variant?: TTooltipVariant
    dropdownActiveStyle?: TTooltipDropdownActiveStyle
    /** Is the tooltip disabled? */
    disabled?: boolean
    /** Should the tooltip toggle on and off on click? */
    toggleOnClick?: boolean
    /** Should there be a delay when rolling off of the target element that prevents the tooltip from hiding right away? (Any value triggers the delay.) */
    delayTag?: string
    /** What size should the text be? */
    textSize?: 'auto' | 'small' | 'default'
    /** Nudge the tooltip horizontally by this many pixels (positive = right, negative = left) */
    horizontalOffset?: number
  }>(),
  {
    variant: 'dark',
    offset: 5,
    textSize: 'auto',
    dropdownActiveStyle: 'default'
  }
)

const emit = defineEmits<{
  (e: 'afterShow'): void
  (e: 'afterHide'): void
}>()

const targetElement: Ref<HTMLElement | null> = ref(null)
const targetRect = ref<DOMRect | undefined>()
const ownsPopup = ref(false)
const focused = computed(() => {
  const element = targetElement.value
  return (
    element?.contains(document.activeElement) ||
    element === document.activeElement
  )
})
const toolTipEventBus = useEventBus(ToolTipEventKey)

const toolTipSpecs = computed(() => {
  return {
    ...props,
    targetRect: targetRect.value
  } as IToolTipSpecifications
})

// tooltip events are distinguished by type
const toolTipShowEvent = computed(() => {
  return {
    type: 'show',
    specs: toolTipSpecs.value
  } as ToolTipEvent
})

const toolTipToggleEvent = computed(() => {
  return {
    type: 'toggle',
    specs: toolTipSpecs.value
  } as ToolTipEvent
})

// either show/hide on click OR on focus change (otherwise these events conflict)
watch(focused, (focused) => {
  if (!props.toggleOnClick && !props.disabled) {
    if (focused) {
      show()
    } else {
      hide()
    }
  }
})

const updateBoundingRectForSpecs = () => {
  if (targetElement.value) {
    targetRect.value = targetElement.value.getBoundingClientRect()
  }
}

const show = () => {
  if (!props.disabled) {
    updateBoundingRectForSpecs()
    toolTipEventBus.emit(toolTipShowEvent.value)
  }
}

const hide = () => {
  toolTipEventBus.emit({ type: 'hide', specs: toolTipSpecs.value })
}

watch(
  () => props.disabled,
  (disabled) => {
    if (disabled && targetRect.value) hide()
  }
)

const onClick = () => {
  if (props.toggleOnClick && !props.disabled) {
    updateBoundingRectForSpecs()
    toolTipEventBus.emit(toolTipToggleEvent.value)
  }
}

const onPointerEnter = (evt: PointerEvent) => {
  if (evt.pointerType === 'mouse' && !props.toggleOnClick) {
    show()
  }
}

// Prevents lingering tooltips when the target is removed from the DOM.
// Only emit if targetRect is set (i.e. the tooltip was actually shown), to avoid
// closing an unrelated tooltip that opened in the same render cycle.
onUnmounted(() => {
  if (targetRect.value) hide()
})

const onPointerLeave = (evt: PointerEvent) => {
  if (evt.pointerType === 'mouse' && !props.toggleOnClick) {
    toolTipEventBus.emit({ type: 'pointerLeaveTarget' })
  }
}

const onFocusIn = () => {
  if (!props.toggleOnClick && !props.disabled) {
    show()
  }
}

const onFocusOut = (evt: FocusEvent) => {
  if (
    document
      .getElementById('floatingToolTip')
      ?.contains(evt.relatedTarget as Node)
  ) {
    return
  }
  if (!props.toggleOnClick && !props.disabled) {
    hide()
  }
}

const onKeydown = (evt: KeyboardEvent) => {
  if (evt.key !== 'Tab' || evt.shiftKey || !props.reference) return
  const tooltipParent = document.querySelector<HTMLElement>(
    '#floatingToolTip .tooltip-parent'
  )
  if (!tooltipParent || tooltipParent.inert) return
  const focusable = tooltipParent.querySelector<HTMLElement>(
    'a[href], button:not([disabled]), [tabindex="0"]'
  )
  if (focusable) {
    evt.preventDefault()
    focusable.focus()
  }
}

toolTipEventBus.on((event) => {
  if (
    event.specs?.targetRect &&
    toolTipSpecs.value?.targetRect &&
    event.specs.targetRect.x === toolTipSpecs.value.targetRect.x &&
    event.specs.targetRect.y === toolTipSpecs.value.targetRect.y
  ) {
    // only emit events if tooltip event matches this instance (not a different tooltip)
    if (event.type === 'afterShow') {
      ownsPopup.value = !!props.reference
      emit('afterShow')
    } else if (event.type === 'afterHide') {
      ownsPopup.value = false
      emit('afterHide')
    }
  }
})
</script>

<template>
  <div
    ref="targetElement"
    class="pointer-events-auto text-center"
    :class="{ 'w-full': !!fullWidthTarget }"
    :aria-owns="ownsPopup ? 'floatingToolTip' : undefined"
    @pointerenter="onPointerEnter"
    @pointerleave="onPointerLeave"
    @click="onClick"
    @focusin="onFocusIn"
    @focusout="onFocusOut"
    @keydown.capture="onKeydown"
  >
    <slot></slot>
  </div>
</template>
